import { useState, useMemo } from "react";
import useAuth from "../../../Hooks/useAuth";
import {
  useGetDevelopmentBoardQuery,
  useGetDevelopmentPullRequestsQuery,
  useRefreshDevelopmentBoardMutation,
  useRefreshDevelopmentPullRequestsMutation,
} from "../../../store";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  ListView,
  ListToolbar,
  Drawer,
} from "../../../Components/shared";
import {
  FaCodeBranch,
  FaColumns,
  FaTasks,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaMinusCircle,
  FaQuestionCircle,
  FaTimes,
  FaExternalLinkAlt,
  FaGithub,
} from "react-icons/fa";
import { FaCodePullRequest } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";

// Geliştirme — GitHub "BayinerIOT Development" panosu + org'daki açık PR'lar.
// Salt-okunur; inceleme / merge GitHub'da. Yetki: development.read.
// Kart / satır tıklanınca önce sağdan detay drawer'ı açılır; GitHub'a
// drawer'daki "GitHub'da Aç" ile gidilir.
// Contract: BayinerIOT-Workspace-/docs/api-contracts/development-board.md

const TABS = [
  { id: "board", label: "Pano", icon: FaColumns },
  { id: "pulls", label: "Açık PR'lar", icon: FaCodePullRequest },
];

// Backlog yok (#183) — sözleşme development-board.md v1.1: 4 sütun.
const COLUMN_STYLE = {
  Todo: "bg-blue-50 text-blue-700",
  "In progress": "bg-amber-50 text-amber-700",
  "In Review": "bg-purple-50 text-purple-700",
  Done: "bg-emerald-50 text-emerald-700",
};

const CI_BADGE = {
  success: { label: "Başarılı", icon: FaCheckCircle, className: "text-emerald-600" },
  failure: { label: "Başarısız", icon: FaTimesCircle, className: "text-red-600" },
  pending: { label: "Çalışıyor", icon: FaClock, className: "text-amber-600" },
  none: { label: "CI yok", icon: FaMinusCircle, className: "text-gray-400" },
  unknown: { label: "Bilinmiyor", icon: FaQuestionCircle, className: "text-gray-400" },
};

const REVIEW_LABEL = {
  APPROVED: { label: "Onaylandı", className: "bg-emerald-50 text-emerald-700" },
  CHANGES_REQUESTED: { label: "Değişiklik istendi", className: "bg-red-50 text-red-700" },
  REVIEW_REQUIRED: { label: "İnceleme bekliyor", className: "bg-amber-50 text-amber-700" },
};

const PR_COLUMNS = [
  { label: "PR", width: "", skeletonWidth: "w-48", skeletonExtra: true },
  { label: "Açan", width: "w-36", skeletonWidth: "w-24" },
  { label: "Yaş", width: "w-20", skeletonWidth: "w-10" },
  { label: "CI", width: "w-32", skeletonWidth: "w-16" },
  { label: "İnceleme", width: "w-40", skeletonWidth: "w-20" },
  { label: "İş", width: "w-44", skeletonWidth: "w-20" },
];

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const ageLabel = (days) => (days === 0 ? "Bugün" : `${days} gün`);

const errorMessage = (error) =>
  error?.data?.message || "GitHub verisi alınamadı, lütfen daha sonra tekrar deneyin.";

// ── Pano ───────────────────────────────────────────────────────────────
function Avatars({ users }) {
  if (!users?.length) return <span className="text-xs text-gray-400">Atanmamış</span>;
  return (
    <div className="flex items-center gap-1">
      {users.map((u) => (
        <span key={u.login} className="flex items-center gap-1 text-xs text-gray-600">
          {u.avatarUrl && (
            <img src={u.avatarUrl} alt="" className="w-5 h-5 rounded-full" />
          )}
          {u.login}
        </span>
      ))}
    </div>
  );
}

function BoardCard({ item, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="block w-full text-left bg-white border border-gray-200 rounded-xl p-3 hover:border-gray-300 hover:shadow-sm transition"
    >
      <div className="text-xs text-gray-500 mb-1">
        {item.repo}#{item.number}
      </div>
      <div className="text-sm font-Medium text-gray-800 leading-snug">{item.title}</div>
      {item.labels?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.labels.map((l) => (
            <span key={l.name} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
              {l.name}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-2">
        <Avatars users={item.assignees} />
        {item.pullRequests?.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <FaCodePullRequest className="w-3 h-3" />
            {item.pullRequests.length}
          </span>
        )}
      </div>
    </button>
  );
}

function BoardView({ columns, onSelect }) {
  return (
    <div className="flex gap-3 overflow-x-auto p-3 sm:p-4 md:p-6 h-full">
      {columns.map((col) => (
        <div key={col.name} className="flex flex-col w-72 shrink-0 bg-gray-50 rounded-2xl">
          <div className="flex items-center justify-between px-3 py-2.5">
            <span className={`px-2.5 py-1 rounded-full text-xs font-SemiBold ${COLUMN_STYLE[col.name]}`}>
              {col.name}
            </span>
            <span className="text-xs text-gray-500">{col.items.length}</span>
          </div>
          <div className="flex flex-col gap-2 px-2 pb-2 overflow-y-auto">
            {col.items.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-6">Kart yok</div>
            ) : (
              col.items.map((item) => (
                <BoardCard
                  key={`${item.repo}#${item.number}`}
                  item={{ ...item, column: col.name }}
                  onSelect={onSelect}
                />
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Açık PR'lar ────────────────────────────────────────────────────────
function CiBadge({ ci }) {
  const b = CI_BADGE[ci] || CI_BADGE.unknown;
  return (
    <span className={`flex items-center gap-1.5 text-sm ${b.className}`}>
      <b.icon className="w-3.5 h-3.5" />
      {b.label}
    </span>
  );
}

function ReviewBadge({ decision, isDraft }) {
  if (isDraft) {
    return <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">Taslak</span>;
  }
  const r = REVIEW_LABEL[decision];
  if (!r) return <span className="text-xs text-gray-400">-</span>;
  return <span className={`px-2 py-0.5 rounded-full text-xs ${r.className}`}>{r.label}</span>;
}

const issueLinks = (issues) =>
  issues?.length
    ? issues.map((i) => `${i.repo}#${i.number}`).join(", ")
    : "-";

// ── Detay drawer'ı ─────────────────────────────────────────────────────
function DetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-100 last:border-b-0">
      <span className="text-xs text-gray-500 shrink-0 pt-0.5">{label}</span>
      <div className="text-sm text-gray-800 text-right min-w-0">{children}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <div className="text-xs font-SemiBold text-gray-500 uppercase tracking-wide mb-2">{title}</div>
      {children}
    </div>
  );
}

function LinkedItem({ href, label, badge }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
    >
      <span className="text-sm text-gray-700 truncate">{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        {badge}
        <FaExternalLinkAlt className="w-3 h-3 text-gray-400" />
      </span>
    </a>
  );
}

const PR_STATE_LABEL = {
  OPEN: { label: "Açık", className: "bg-emerald-50 text-emerald-700" },
  MERGED: { label: "Birleşti", className: "bg-purple-50 text-purple-700" },
  CLOSED: { label: "Kapalı", className: "bg-gray-100 text-gray-600" },
};

function PrStateBadge({ state, isDraft }) {
  if (isDraft && state === "OPEN") {
    return <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">Taslak</span>;
  }
  const s = PR_STATE_LABEL[state];
  if (!s) return null;
  return <span className={`px-2 py-0.5 rounded-full text-xs ${s.className}`}>{s.label}</span>;
}

function DetailDrawer({ open, onClose, heading, title, url, children }) {
  return (
    <Drawer open={open} onClose={onClose} width="md">
      <Drawer.Header className="px-5 py-4 bg-slate-50/80 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-gray-500">{heading}</div>
            <h2 className="text-base font-SemiBold text-gray-800 leading-snug mt-0.5">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors"
            aria-label="Kapat"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </Drawer.Header>
      <Drawer.Body className="px-5 py-4 flex flex-col gap-5">{children}</Drawer.Body>
      <Drawer.Footer className="px-5 py-3 border-t border-gray-100 bg-white">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-Medium hover:bg-gray-800 transition"
        >
          <FaGithub className="w-4 h-4" />
          GitHub'da Aç
        </a>
      </Drawer.Footer>
    </Drawer>
  );
}

function IssueDetail({ item }) {
  return (
    <>
      <DetailSection title="Bilgiler">
        <DetailRow label="Durum">
          <span className={`px-2.5 py-1 rounded-full text-xs font-SemiBold ${COLUMN_STYLE[item.column] || ""}`}>
            {item.column}
          </span>
        </DetailRow>
        <DetailRow label="Atanan">
          <div className="flex justify-end">
            <Avatars users={item.assignees} />
          </div>
        </DetailRow>
        <DetailRow label="Oluşturulma">{formatTime(item.createdAt)}</DetailRow>
        <DetailRow label="Son güncelleme">{formatTime(item.updatedAt)}</DetailRow>
        {item.closedAt && <DetailRow label="Kapanış">{formatTime(item.closedAt)}</DetailRow>}
      </DetailSection>

      {item.labels?.length > 0 && (
        <DetailSection title="Etiketler">
          <div className="flex flex-wrap gap-1.5">
            {item.labels.map((l) => (
              <span key={l.name} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                {l.name}
              </span>
            ))}
          </div>
        </DetailSection>
      )}

      <DetailSection title="Bağlı PR'lar">
        {item.pullRequests?.length ? (
          <div className="flex flex-col gap-2">
            {item.pullRequests.map((pr) => (
              <LinkedItem
                key={`${pr.repo}#${pr.number}`}
                href={pr.url}
                label={`${pr.repo}#${pr.number}`}
                badge={<PrStateBadge state={pr.state} isDraft={pr.isDraft} />}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400">Bu işi kapatacak PR yok</div>
        )}
      </DetailSection>
    </>
  );
}

function PullRequestDetail({ pr }) {
  return (
    <>
      <DetailSection title="Bilgiler">
        <DetailRow label="Açan">
          <div className="flex justify-end">
            <Avatars users={[pr.author]} />
          </div>
        </DetailRow>
        <DetailRow label="Yaş">{ageLabel(pr.ageDays)}</DetailRow>
        <DetailRow label="CI">
          <div className="flex justify-end">
            <CiBadge ci={pr.ci} />
          </div>
        </DetailRow>
        <DetailRow label="İnceleme">
          <ReviewBadge decision={pr.reviewDecision} isDraft={pr.isDraft} />
        </DetailRow>
        <DetailRow label="Oluşturulma">{formatTime(pr.createdAt)}</DetailRow>
        <DetailRow label="Son güncelleme">{formatTime(pr.updatedAt)}</DetailRow>
      </DetailSection>

      <DetailSection title="Kapatacağı işler">
        {pr.issues?.length ? (
          <div className="flex flex-col gap-2">
            {pr.issues.map((i) => (
              <LinkedItem key={`${i.repo}#${i.number}`} href={i.url} label={`${i.repo}#${i.number}`} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400">Bağlı iş yok</div>
        )}
      </DetailSection>
    </>
  );
}

// ── Sayfa ──────────────────────────────────────────────────────────────
function DevelopmentPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const [tab, setTab] = useState("board");
  // { type: "issue" | "pr", data } — kapanırken Drawer içeriği kendisi dondurur.
  const [selected, setSelected] = useState(null);
  const closeDetail = () => setSelected(null);

  const board = useGetDevelopmentBoardQuery({ token }, { skip: !token });
  const pulls = useGetDevelopmentPullRequestsQuery({ token }, { skip: !token });
  const [refreshBoard, refreshBoardState] = useRefreshDevelopmentBoardMutation();
  const [refreshPulls, refreshPullsState] = useRefreshDevelopmentPullRequestsMutation();

  const columns = useMemo(() => board.data?.data?.columns || [], [board.data]);
  const pullRequests = useMemo(() => pulls.data?.data?.pullRequests || [], [pulls.data]);
  const active = tab === "board" ? board : pulls;
  const refreshing = refreshBoardState.isLoading || refreshPullsState.isLoading;
  const loading = active.isLoading || active.isFetching || refreshing;

  const openIssueCount = useMemo(
    () => columns.filter((c) => c.name !== "Done").reduce((n, c) => n + c.items.length, 0),
    [columns],
  );
  const inReviewCount = columns.find((c) => c.name === "In Review")?.items.length || 0;

  const stats = useMemo(
    () => [
      { icon: FaTasks, value: openIssueCount, label: "Açık iş", color: "blue" },
      { icon: FaCheckCircle, value: inReviewCount, label: "İnceleme bekleyen iş", color: "indigo" },
      { icon: FaCodePullRequest, value: pullRequests.length, label: "Açık PR", color: "amber" },
    ],
    [openIssueCount, inReviewCount, pullRequests.length],
  );

  const handleRefresh = () => {
    if (!token) return;
    refreshBoard({ token });
    refreshPulls({ token });
  };

  const fetchedAt = active.data?.data?.fetchedAt;

  const loadingNode = (
    <div className="relative p-3 sm:p-4 md:p-6">
      <ListSkeleton
        columns={PR_COLUMNS}
        rowCount={8}
        mobileCardCount={5}
        message="GitHub verisi yükleniyor..."
      />
    </div>
  );

  const errorNode = (
    <div className="p-4 md:p-6">
      <EmptyState message="GitHub verisi alınamadı" suggestion={errorMessage(active.error)} />
    </div>
  );

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
        <PageHeader
          icon={FaCodeBranch}
          iconColor="blue"
          title="GitHub"
          subtitle="GitHub panosu ve açık PR'lar — salt okunur, inceleme GitHub'da"
          stats={stats}
          isLoading={loading}
          mobileSlotId="development-toolbar-mobile-slot"
          actions={[
            { label: "Yenile", icon: FiRefreshCw, onClick: handleRefresh, disabled: refreshing },
          ]}
        />
      </div>

      <div className="bg-white flex-1 min-h-0 flex flex-col">
        <ListToolbar
          className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
          mobileFilterSlotId="development-toolbar-mobile-slot"
          filterTabs={{
            filters: TABS,
            activeFilter: tab,
            onChange: setTab,
            isLoading: loading,
            color: "blue",
          }}
          searchNote={
            fetchedAt
              ? `Son güncelleme: ${formatTime(fetchedAt)}${active.data?.data?.cached ? " (önbellek)" : ""}`
              : undefined
          }
        />

        {tab === "board" ? (
          loading && !columns.length ? (
            loadingNode
          ) : board.isError ? (
            errorNode
          ) : (
            <div className="flex-1 min-h-0">
              <BoardView columns={columns} onSelect={(item) => setSelected({ type: "issue", data: item })} />
            </div>
          )
        ) : pulls.isError ? (
          errorNode
        ) : (
          <ListView
            headers={PR_COLUMNS.map((c) => ({ label: c.label, className: c.width }))}
            data={pullRequests}
            rowKey={(pr) => `${pr.repo}#${pr.number}`}
            renderRow={(pr) => (
              <tr
                key={`${pr.repo}#${pr.number}`}
                className="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelected({ type: "pr", data: pr })}
              >
                <td className="px-4 py-3">
                  <div className="text-xs text-gray-500">{pr.repo}#{pr.number}</div>
                  <div className="text-sm font-Medium text-gray-800">{pr.title}</div>
                </td>
                <td className="px-4 py-3">
                  <Avatars users={[pr.author]} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{ageLabel(pr.ageDays)}</td>
                <td className="px-4 py-3">
                  <CiBadge ci={pr.ci} />
                </td>
                <td className="px-4 py-3">
                  <ReviewBadge decision={pr.reviewDecision} isDraft={pr.isDraft} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{issueLinks(pr.issues)}</td>
              </tr>
            )}
            renderMobileCard={(pr) => (
              <button
                type="button"
                key={`${pr.repo}#${pr.number}`}
                onClick={() => setSelected({ type: "pr", data: pr })}
                className="block w-[calc(100%-1.5rem)] text-left bg-white border border-gray-200 rounded-xl p-3 mx-3 mb-2"
              >
                <div className="text-xs text-gray-500">{pr.repo}#{pr.number} · {ageLabel(pr.ageDays)}</div>
                <div className="text-sm font-Medium text-gray-800 mt-0.5">{pr.title}</div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <Avatars users={[pr.author]} />
                  <CiBadge ci={pr.ci} />
                </div>
                <div className="mt-2">
                  <ReviewBadge decision={pr.reviewDecision} isDraft={pr.isDraft} />
                </div>
              </button>
            )}
            isLoading={loading}
            loadingNode={loadingNode}
            empty={
              <div className="p-4 md:p-6">
                <EmptyState message="Açık PR yok" suggestion="Org'da inceleme bekleyen PR bulunmuyor." />
              </div>
            }
          />
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={closeDetail}
        heading={selected ? `${selected.data.repo}#${selected.data.number}` : ""}
        title={selected?.data.title}
        url={selected?.data.url}
      >
        {selected?.type === "issue" && <IssueDetail item={selected.data} />}
        {selected?.type === "pr" && <PullRequestDetail pr={selected.data} />}
      </DetailDrawer>
    </PageShell>
  );
}

export default DevelopmentPage;
